import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as MenuDotsIcon } from '@assets/images/Icons/Contextual_menu_dots.svg'
import { ReactComponent as MenuCaretIcon } from '@assets/images/Icons/Contextual_menu_caret.svg'
import type {
  ContextualMenuItem,
  ContextualMenuProps,
} from './ContextualMenu.types'
import './ContextualMenu.css'

type MenuPosition = {
  top: number
  left: number
}

const MENU_GAP = 8

const ContextualMenu: React.FC<ContextualMenuProps> = ({
  items,
  ariaLabel = 'Open menu',
  open,
  defaultOpen = false,
  onOpenChange,
  darkMode = false,
  previewState,
  className = '',
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null)
  const isPreview = previewState != null
  const isControlled = open !== undefined
  const isOpen = isPreview
    ? previewState !== 'default'
    : isControlled
      ? open
      : internalOpen

  const previewSubmenuId =
    previewState === 'expanded'
      ? (items.find((item) => item.items && item.items.length > 0)?.id ?? null)
      : null

  const activeSubmenuId = isPreview ? previewSubmenuId : openSubmenuId

  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (!isControlled) {
        setInternalOpen(nextOpen)
      }
      if (!nextOpen) {
        setOpenSubmenuId(null)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange],
  )

  const updatePosition = useCallback((): void => {
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) {
      return
    }

    const triggerRect = trigger.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()

    let left = triggerRect.right - menuRect.width
    if (left < MENU_GAP) {
      left = triggerRect.left
    }

    let top = triggerRect.bottom + MENU_GAP
    if (top + menuRect.height > window.innerHeight - MENU_GAP) {
      top = triggerRect.top - MENU_GAP - menuRect.height
    }

    setPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (isPreview || !isOpen) {
      setPosition(null)
      return
    }

    updatePosition()
  }, [isOpen, isPreview, updatePosition, activeSubmenuId])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handleReposition = (): void => updatePosition()

    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, isPreview, updatePosition])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [isOpen, isPreview, setOpen])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (openSubmenuId) {
          setOpenSubmenuId(null)
          return
        }
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isPreview, openSubmenuId, setOpen])

  const classNames = [
    'ds-contextual-menu',
    isOpen ? 'ds-contextual-menu--open' : '',
    darkMode ? 'ds-contextual-menu--dark' : '',
    isPreview ? 'ds-contextual-menu--preview' : '',
    previewState ? `ds-contextual-menu--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const menuClassNames = [
    'ds-contextual-menu__menu',
    darkMode ? 'ds-contextual-menu__menu--dark' : '',
    activeSubmenuId ? 'ds-contextual-menu__menu--submenu-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const renderItem = (
    item: ContextualMenuItem,
    index: number,
  ): React.ReactNode => {
    const hasSubmenu = Boolean(item.items && item.items.length > 0)
    const submenuOpen = hasSubmenu && activeSubmenuId === item.id
    const previewHover = isPreview && previewState === 'hover' && index === 0
    const previewExpandedParent =
      isPreview && previewState === 'expanded' && submenuOpen

    const itemClassNames = [
      'ds-contextual-menu__item',
      hasSubmenu ? 'ds-contextual-menu__item--has-submenu' : '',
      submenuOpen || previewExpandedParent
        ? 'ds-contextual-menu__item--submenu-open'
        : '',
      previewHover ? 'ds-contextual-menu__item--preview-hover' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div
        key={item.id}
        className="ds-contextual-menu__item-wrap"
        onMouseEnter={() => {
          if (isPreview || item.disabled) return
          if (hasSubmenu) {
            setOpenSubmenuId(item.id)
          } else {
            setOpenSubmenuId(null)
          }
        }}
      >
        <button
          type="button"
          role="menuitem"
          className={itemClassNames}
          disabled={item.disabled}
          tabIndex={isPreview ? -1 : undefined}
          aria-haspopup={hasSubmenu ? 'menu' : undefined}
          aria-expanded={hasSubmenu ? submenuOpen : undefined}
          onClick={() => {
            if (isPreview || item.disabled) {
              return
            }
            if (hasSubmenu) {
              setOpenSubmenuId((current) =>
                current === item.id ? null : item.id,
              )
              return
            }
            item.onSelect?.()
            setOpen(false)
          }}
        >
          {item.icon ? (
            <span className="ds-contextual-menu__item-icon" aria-hidden="true">
              {item.icon}
            </span>
          ) : null}
          <span className="ds-contextual-menu__item-label">{item.label}</span>
          {hasSubmenu ? (
            <MenuCaretIcon
              className="ds-contextual-menu__submenu-caret"
              aria-hidden="true"
            />
          ) : null}
        </button>

        {submenuOpen && item.items ? (
          <div
            className={[
              'ds-contextual-menu__submenu',
              darkMode ? 'ds-contextual-menu__submenu--dark' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="menu"
          >
            {item.items.map((subItem, subIndex) => {
              const previewSubHover =
                isPreview && previewState === 'expanded' && subIndex === 0
              return (
                <button
                  key={subItem.id}
                  type="button"
                  role="menuitem"
                  className={[
                    'ds-contextual-menu__item',
                    previewSubHover
                      ? 'ds-contextual-menu__item--preview-hover'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={subItem.disabled}
                  tabIndex={isPreview ? -1 : undefined}
                  onClick={() => {
                    if (isPreview || subItem.disabled) {
                      return
                    }
                    subItem.onSelect?.()
                    setOpen(false)
                  }}
                >
                  {subItem.icon ? (
                    <span
                      className="ds-contextual-menu__item-icon"
                      aria-hidden="true"
                    >
                      {subItem.icon}
                    </span>
                  ) : null}
                  <span className="ds-contextual-menu__item-label">
                    {subItem.label}
                  </span>
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
    )
  }

  const menuItems = <>{items.map((item, index) => renderItem(item, index))}</>

  return (
    <div className={classNames} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="ds-contextual-menu__trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        tabIndex={isPreview ? -1 : undefined}
        data-preview-state={previewState}
        onClick={() => {
          if (!isPreview) {
            setOpen(!isOpen)
          }
        }}
      >
        <MenuDotsIcon className="ds-contextual-menu__icon" aria-hidden="true" />
      </button>

      {isOpen && isPreview ? (
        <div className={menuClassNames} role="menu">
          {menuItems}
        </div>
      ) : null}

      {isOpen && !isPreview
        ? createPortal(
            <div
              ref={menuRef}
              className={`${menuClassNames} ds-contextual-menu__menu--floating`}
              role="menu"
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                visibility: position ? 'visible' : 'hidden',
              }}
              onMouseLeave={() => setOpenSubmenuId(null)}
            >
              {menuItems}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

export default ContextualMenu
